import { useSpring } from 'react-spring/three'
import { useEffect } from 'react'
import { useGesture } from 'react-use-gesture'

const defaultOptions = {
  aspect: 1,
  elevation: 2
}

/**
 * Creates handler to manipulate position based on
 * drag events
 * @param {*} position
 * @param {*} onMove
 * @param {*} options
 */
const makeDragPosition = (
  position,
  onMove,
  options = {}
) => {
  options = { ...defaultOptions, ...options }
  return ({
    delta: [x, y],
    down,
    memo = position.getValue()
  }) => {
    const elevation = down ? 1 : 0
    let newPosition = [
      memo[0] + x / options.aspect,
      options.elevation + elevation,
      memo[2] + y / options.aspect
    ]
    onMove({
      position: newPosition
    })
    return newPosition
  }
}

const useDragPosition = (
  initialPosition,
  handleRest,
  options = {}
) => {
  const aspect = options.aspect || 1
  // use a spring for animating piece position
  const [{ position }, setPosition] = useSpring(() => ({
    position: initialPosition,
    config: {
      mass: 1,
      tension: 210,
      friction: 20
    }
  }))

  // update the onRest handler on the spring when
  // there are changes to the puzzle state
  useEffect(() => {
    setPosition({
      onRest(state) {
        handleRest(state.position)
      }
    })
  })

  // update position when initial position changes
  useEffect(() => {
    setPosition({ position: initialPosition })
  }, [initialPosition, setPosition])

  // get a function that updates position on drag
  const dragHandler = makeDragPosition(
    position,
    setPosition,
    { aspect }
  )

  // create event bindings for drag events
  const bind = useGesture(
    { onDrag: dragHandler },
    { eventOptions: { pointer: true } }
  )

  return [bind, position]
}

export default useDragPosition
